import { filterEmpty } from '@fox/atoms/utils/filter-empty';
import { INTERVAL_NAMESLY } from '@fox/finance/src/contracts';
import { QuestionStyle } from '../../form-runtime-context/contracts';
import { BuildStylesType, Plan, SubscriptionAnswer, SubscriptionFieldMeta } from './contracts';

export const PLAN_OPTION_LABEL = 'PLAN_OPTION_LABEL';

export const DEFAULT_SUBSCRIPTION_BLOCK_METADATA: SubscriptionFieldMeta = {
	allowedPlans: null,
	required: false,
	labelOverrides: {
		planKey: null,
		interval: null
	},
	showAutoRenew: true,
	defaultToAutoRenew: true,
	provides: { select: [] },
	immediatePayment: true,
	optionDisplayType: 'Checkbox'
};

/**
 * Creates a friendly plan option label in the format of
 * If no units: Journey Insurance - $30.16 / Annual
 * If has units: Journey Insurance - $15.08 / 6 Monthly
 * @param option - Plan option
 * @returns Friendly option label
 */
export function createPlanOptionLabel(option: Plan['options'][0]) {
	const hasUnits = option.units > 0;
	const intervalLabel = hasUnits ? `${option.units} ${INTERVAL_NAMESLY[option.interval]}` : INTERVAL_NAMESLY[option.interval];
	return `${option.name} - $${option.amount / 100} / ${intervalLabel} `;
}
export const findAllPlanOptions = (plans: Plan[]) => {
	const allPlanOptions = plans
		?.flatMap(p => p.options)
		?.reduce((acc: Plan['options'], cur: Plan['options'][0]) => {
			if (!acc.find(o => o.id === cur.id)) {
				acc.push(cur);
			}
			return acc;
		}, []);
	return allPlanOptions ?? [];
};
export function encodePlanOptionLabelId(id: number) {
	return `plan_option_${id}`;
}

export const buildStyles = (labelStyle: QuestionStyle, answerStyle: QuestionStyle): BuildStylesType => {
	const labelStyles = {
		'&.MuiTypography-root.MuiTypography-body1': {
			...labelStyle
		}
	};

	const answerStyles = {
		'& .MuiTypography-root.MuiTypography-body1': {
			...answerStyle
		}
	};

	return { labelStyles, answerStyles };
};
/**
 * Finds new matching options by comparing name/id with available options on new plan.
 * Also checks to make sure interval and unit requirements on option match
 * @param allPlanOptions - All plan options
 * @param fieldMeta - Block's metadata
 * @param answers - Filled in subscription answer
 * @param newFee - New fee / interval being changed to
 * @param newPlan - New plan if plan is being changed
 * @returns
 */
export const findNewOptions = (
	allPlanOptions: Plan['options'],
	fieldMeta: SubscriptionFieldMeta,
	answers: SubscriptionAnswer,
	newFee?: Plan['fees'][0],
	newPlan?: Plan
) => {
	const oldOptionsMapped = answers.optionIds?.map(o => allPlanOptions?.find(option => option.id === o)) ?? [];
	const newOptionsMapped = fieldMeta.allowedOptions?.[newPlan?.planKey]?.map(o => allPlanOptions?.find(option => option.id === o)) ?? [];
	let newOptions: number[] = [];
	if (newPlan) {
		newOptions = oldOptionsMapped
			.filter(oldOption => {
				const compatibleOption = newOptionsMapped.find(
					newOption =>
						(newOption.name === oldOption.name || newOption.id === oldOption.id) && checkIsCompatiblePlanOption(newFee, newOption)
				);
				if (compatibleOption) return true;
				else return false;
			})
			.map(o => o.id);
	} else {
		newOptions = oldOptionsMapped.filter(oldOption => checkIsCompatiblePlanOption(newFee, oldOption)).map(o => o.id);
	}
	const defaultOptions = fieldMeta.defaultOptions?.[newPlan?.planKey ?? answers.planKey];
	if (defaultOptions && newFee) {
		for (const o of defaultOptions) {
			const optionDetails = allPlanOptions?.find(option => option.id === o);
			if (checkIsCompatiblePlanOption(newFee, optionDetails) && !newOptions.includes(o)) {
				newOptions.push(o);
			}
		}
	}
	return filterEmpty(newOptions);
};
/**
 * Check if option is compatible with current fee interval and units
 * @param fee - The selected plan fee
 * @param option - The selected plan option
 * @returns True if compatible else false
 */
export const checkIsCompatiblePlanOption = (fee: Plan['fees'][0], option?: Plan['options'][0]) => {
	if (!option) return false;
	return (fee?.interval === option.interval || option.interval === null) && (option.units ?? 1) === (fee?.units ?? 1);
};

/**
 * Returns the allowed fee intervals for a selected plan, filtered by the allowed fee IDs in fieldMeta.
 *
 * @param fieldMeta - The subscription block metadata containing allowed fee IDs.
 * @param selectedPlan - The target plan.
 * @returns An array of allowed fee intervals for the selected plan.
 */
export const getAllowedFees = (fieldMeta: SubscriptionFieldMeta, selectedPlan?: Plan | null) => {
	if (!selectedPlan?.fees) return [];
	if (!fieldMeta.fees?.restrict) {
		return selectedPlan.fees;
	}
	const allowedFees: Plan['fees'] = selectedPlan.fees?.filter(f => fieldMeta.fees?.allowedFees?.[selectedPlan.id]?.includes(f.id) ?? false);

	return allowedFees ?? [];
};
